(() => {
  const config = window.posthogConfig;
  if (!config?.enabled) return;

  if (!config.projectToken) {
    if (config.designMode) {
      console.error(
        'settings.posthog_project_token variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once settings.posthog_project_token is configured'
      );
    }
    return;
  }

  let analyticsAllowed = false;
  let initialized = false;
  let initializing = false;
  let sdkLoadingPromise;
  let consentRevision = 0;
  let productViewed = false;
  const pendingEvents = [];

  window.impalaPostHog = {
    isAllowed: () => analyticsAllowed,
    consentRevision: () => consentRevision,
    capture(event, properties) {
      if (!analyticsAllowed) return;
      if (!initialized) {
        if (pendingEvents.length < 50) pendingEvents.push({ event, properties });
        return;
      }
      try {
        window.posthog.capture(event, properties);
      } catch (error) {
        console.warn('Impala PostHog event could not be captured.');
      }
    },
  };

  function captureProductView() {
    if (!analyticsAllowed || !config.product || productViewed) return;
    productViewed = true;
    window.impalaPostHog.capture('product_viewed', config.product);
  }

  function stopCapturing() {
    if (!window.posthog) return;
    window.posthog.opt_out_capturing({ clear_persistence: true });
  }

  function initializePostHog() {
    if (!analyticsAllowed || initializing) return;

    if (initialized) {
      window.posthog.opt_in_capturing();
      captureProductView();
      return;
    }

    initializing = true;
    window.posthog.init(config.projectToken, {
      api_host: config.apiHost,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      person_profiles: 'identified_only',
      loaded(posthog) {
        posthog.register({
          'service.name': 'impala-storefront',
          'service.namespace': 'impala',
          'deployment.environment.name': config.environment,
        });
        initialized = true;
        initializing = false;
        if (!analyticsAllowed) {
          stopCapturing();
          return;
        }
        posthog.opt_in_capturing();
        captureProductView();
        for (const { event, properties } of pendingEvents.splice(0)) {
          window.impalaPostHog.capture(event, properties);
        }
      },
      session_recording: {
        maskAllInputs: true,
      },
    });
  }

  function loadPostHog() {
    if (window.posthog) {
      initializePostHog();
      return Promise.resolve();
    }
    if (sdkLoadingPromise) return sdkLoadingPromise;

    sdkLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      // Keep this unversioned endpoint so PostHog can serve current browser SDK releases.
      script.src = `${config.apiHost}/static/array.js`;
      script.onload = () => {
        initializePostHog();
        resolve();
      };
      script.onerror = () => {
        sdkLoadingPromise = undefined;
        reject(new Error('Unable to load the PostHog browser SDK.'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      console.error(error);
    });

    return sdkLoadingPromise;
  }

  function updateAnalyticsConsent() {
    const allowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    if (allowed !== analyticsAllowed) consentRevision += 1;
    analyticsAllowed = allowed;
    if (analyticsAllowed) {
      loadPostHog();
    } else {
      pendingEvents.length = 0;
      stopCapturing();
    }
  }

  function loadCustomerPrivacyApi() {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) {
          console.error('Unable to load Shopify Customer Privacy API for PostHog.', error);
          return;
        }
        updateAnalyticsConsent();
      }
    );
  }

  if (window.Shopify?.loadFeatures) {
    loadCustomerPrivacyApi();
  } else {
    document.addEventListener('DOMContentLoaded', loadCustomerPrivacyApi, { once: true });
  }
  document.addEventListener('visitorConsentCollected', updateAnalyticsConsent);
})();
